import React, {
	useCallback,
	useMemo,
} from 'react';
import { ColumnsType } from 'antd/es/table/InternalTable';
import { Flex } from 'antd';
import {
	DATE,
	LaytimeTerms,
	TIME,
} from '@shared/utils/constants';
import {
	formatDuration,
	toMoment,
} from '@shared/utils/date';
import { calculateTotal } from '@shared/utils/math';
import {
	isDischargePort,
	isLoadPort,
} from '@shared/utils/cargoPortHelpers';
import type {
	CargoPortWithTurnDays,
	CargoWithPortsAndCalculationsJSON,
} from '@api/features/laytime/getCargosAndLaytimes';
import type { TimesheetRowProps } from '@api/models/timesheet-row';
import Card from '@client/components/Card/Card';
import Table from '@client/components/Table/Table';
import { formatDate } from '@client/utils/formatDate';
import CountryFlag from '@client/components/CountryFlag';
import styles from './Timesheets.module.css';

type Props = {
	selectedCargo: CargoWithPortsAndCalculationsJSON;
	selectedCargoPort: CargoPortWithTurnDays;
	isLoading: boolean;
};

const Timesheets = ({
	selectedCargo,
	selectedCargoPort,
	isLoading,
}: Props) => {
	const checkForComments = useCallback(() => {
		switch (selectedCargo.laytimeTerms) {
			case LaytimeTerms.AVERAGED:
			case LaytimeTerms.REVERSIBLE: {
				const allRows = selectedCargo?.Timesheets?.flatMap((ts) => ts?.TimesheetRows) ?? [];

				return allRows?.some((s) => s.comment != null);
			}

			default:
			case LaytimeTerms.NON_REVERSIBLE: {
				const rows = selectedCargoPort.Timesheet?.TimesheetRows;

				return rows?.some((s) => s.comment != null);
			}
		}
	}, [
		selectedCargo?.Timesheets,
		selectedCargo.laytimeTerms,
		selectedCargoPort.Timesheet?.TimesheetRows,
	]);

	const columns = useMemo(() => {
		const hasComments = checkForComments();

		return [
			{
				dataIndex: 'description',
				title: 'Description',
				width: 150,
			},
			...(hasComments ?
				[
					{
						dataIndex: 'comment',
						title: 'Comment',
						width: 150,
						render: (c: string | null) => c ?? '-',
					},
				] : []),
			{
				dataIndex: 'date',
				title: 'Date',
				type: 'date',
				render: (v) => formatDate(v, DATE),
			},
			{
				dataIndex: 'weekday',
				title: 'Weekday',
				render: (_, row) => toMoment(row.date).format('dddd'),
			},
			{
				dataIndex: 'from',
				title: 'Time from',
				render: (v, row) => (row.demurrageStarts ? '-' : formatDate(v, TIME, false)),
			},
			{
				dataIndex: 'to',
				title: 'Time to',
				render: (v) => formatDate(v, TIME, false),
			},
			{
				dataIndex: 'eventTimeTotal',
				title: 'Event time total',
				render: (_, row) => {
					if (row.demurrageStarts) {
						return '';
					}

					return formatDuration({ rawStart: row.from, rawEnd: row.to });
				},
			},
			{
				dataIndex: 'percentCounting',
				title: '% counting',
				render: (v, row) => (row.demurrageStarts ? '' : `${v}%`),
			},
			{
				dataIndex: 'totalTimeSpent',
				title: 'Total time spent',
				render: (_, row) => {
					if (row.demurrageStarts) {
						return '';
					}

					if (selectedCargo.laytimeTerms === LaytimeTerms.NON_REVERSIBLE) {
						const rows = selectedCargoPort
							?.Timesheet
							?.TimesheetRows;

						if (!rows) {
							return 'N/A';
						}

						const rowIndex = rows.findIndex((r) => r.id === row.id);
						const relevantRows = rows.slice(0, rowIndex + 1);
						const totalMinutes = calculateTotal(relevantRows, (r) => r.totalCountingMinutes);

						return formatDuration({ rawMinutes: totalMinutes });
					}

					if (selectedCargo.laytimeTerms === LaytimeTerms.REVERSIBLE) {
						const allRows = selectedCargo.Timesheets.flatMap((ts) => ts.TimesheetRows);

						const rowIndex = allRows.findIndex((r) => r.id === row.id);
						const relevantRows = allRows.slice(0, rowIndex + 1);

						const totalMinutes = calculateTotal(relevantRows, (r) => r.totalCountingMinutes);

						return formatDuration({ rawMinutes: totalMinutes });
					}

					if (selectedCargo.laytimeTerms === LaytimeTerms.AVERAGED) {
						const isLoadingPort = isLoadPort(selectedCargoPort);
						const filterFunc = isLoadingPort ? isLoadPort : isDischargePort;
						const sortedCargoPorts = selectedCargo.CargoPorts.filter(filterFunc);
						const relevantCpIds = sortedCargoPorts.map((cp) => cp.id);
						const relevantTimesheets = selectedCargo.Timesheets
							.filter((ts) => relevantCpIds.includes(ts.cargoPortId!));

						const timesheetRows = relevantTimesheets.flatMap((ts) => ts.TimesheetRows)
							.filter((tr) => tr != null);

						const rowIndex = timesheetRows.findIndex((r) => r.id === row.id);
						const relevantRows = timesheetRows.slice(0, rowIndex + 1);

						const totalMinutes = calculateTotal(relevantRows, (r) => r.totalCountingMinutes);

						return formatDuration({ rawMinutes: totalMinutes });
					}

					return '';
				},
			},
		] as ColumnsType<TimesheetRowProps>;
	}, [
		checkForComments,
		selectedCargo.laytimeTerms,
		selectedCargo.Timesheets,
		selectedCargo.CargoPorts,
		selectedCargoPort,
	]);

	if (!selectedCargo || !selectedCargoPort) {
		return null;
	}

	switch (selectedCargo.laytimeTerms) {
		case LaytimeTerms.REVERSIBLE: {
			const sortedCargoPorts = selectedCargo.CargoPorts;
			const timesheets = selectedCargo.Timesheets.sort((a, b) => {
				const indexA = sortedCargoPorts.findIndex((cp) => cp.id === a.cargoPortId);
				const indexB = sortedCargoPorts.findIndex((cp) => cp.id === b.cargoPortId);

				return indexA - indexB;
			});

			return (
				<>
					{timesheets.map((timesheet) => {
						const cargoPort = selectedCargo.CargoPorts
							.find((cp) => cp.id === timesheet.cargoPortId);

						const isSelectedPort = cargoPort?.id === selectedCargoPort.id;

						return (
							<div style={{ position: 'relative' }}>
								<div className={isSelectedPort ? undefined : styles.disabledCard} />
								<Card
									title={(
										<Flex gap={15} align="center">
											<p style={{ margin: 0 }}>Timesheet</p>
											<Flex gap={5}>
												<CountryFlag countryCode={cargoPort?.port.countryCode} />
												<p style={{ margin: 0 }}>{`${cargoPort?.port.name}`}</p>
											</Flex>
										</Flex>
									)}
									padding={0}
									size="small"
								>
									<Table
										dataSource={timesheet.TimesheetRows ?? []}
										size="small"
										columns={columns}
										pagination={false}
										rowClassName={(row) => (row.demurrageStarts ? styles.demurrageRow : '')}
									/>
								</Card>
							</div>
						);
					})}
				</>
			);
		}

		case LaytimeTerms.AVERAGED: {
			const filterFunc = isLoadPort(selectedCargoPort) ? isLoadPort : isDischargePort;
			const sortedCargoPorts = selectedCargo.CargoPorts.filter(filterFunc);
			const relevantCpIds = sortedCargoPorts.map((cp) => cp.id);
			const timesheets = selectedCargo.Timesheets.sort((a, b) => {
				const indexA = sortedCargoPorts.findIndex((cp) => cp.id === a.cargoPortId);
				const indexB = sortedCargoPorts.findIndex((cp) => cp.id === b.cargoPortId);

				return indexA - indexB;
			}).filter((ts) => relevantCpIds.includes(ts.cargoPortId!));

			return (
				<>
					{timesheets.map((timesheet) => {
						const cargoPort = selectedCargo.CargoPorts
							.find((cp) => cp.id === timesheet.cargoPortId);

						const isSelectedPort = cargoPort?.id === selectedCargoPort.id;

						return (
							<div style={{ position: 'relative' }}>
								<div className={isSelectedPort ? undefined : styles.disabledCard} />
								<Card
									title={(
										<Flex gap={15} align="center">
											<p style={{ margin: 0 }}>Timesheet</p>
											<Flex gap={5}>
												<CountryFlag countryCode={cargoPort?.port.countryCode} />
												<p style={{ margin: 0 }}>{`${cargoPort?.port.name}`}</p>
											</Flex>
										</Flex>
									)}
									padding={0}
									size="small"
								>
									<Table
										dataSource={timesheet.TimesheetRows ?? []}
										size="small"
										columns={columns}
										pagination={false}
										rowClassName={(row) => (row.demurrageStarts ? styles.demurrageRow : '')}
									/>
								</Card>
							</div>

						);
					})}
				</>
			);
		}

		default:
		case LaytimeTerms.NON_REVERSIBLE:
			return (
				<Card
					title="Timesheet"
					padding={0}
					size="small"
				>
					<Table
						dataSource={selectedCargoPort?.Timesheet?.TimesheetRows ?? []}
						size="small"
						columns={columns}
						pagination={false}
						rowClassName={(row) => (row.demurrageStarts ? styles.demurrageRow : '')}
						emptyText="No timesheet calculated yet"
						loading={isLoading}
					/>
				</Card>
			);
	}
};

export default Timesheets;
